/**
 * useFutureBotAttachments — pending-attachment state for the FutureBot composer.
 *
 * Files upload to Cloud Storage the moment they're picked (paperclip, paste,
 * or drag-drop) at futurebot-uploads/{uid}/{sessionId}/{ts}_{name}. The chat
 * transcript then only carries the tiny gs:// URI — Gemini on Vertex reads the
 * bytes straight from the bucket (the backend re-validates that every URI sits
 * under the caller's own prefix).
 *
 * Attachment shape (matches what useFutureBotAgent.send expects):
 *   { id, name, size, mimeType, status: 'uploading'|'done', progress,
 *     storagePath, gsUri, downloadURL }
 *
 * Failed uploads are removed from the list with a toast — callers only ever
 * see 'uploading' or 'done' entries.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { firebase, firebaseConfig } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeImageForChat } from '../../utils/compressImage';

export const MAX_ATTACHMENTS = 4;
const MAX_PDF_BYTES = 20 * 1024 * 1024; // storage rule cap

const generateId = () => crypto.randomUUID();

function safeFileName(name) {
  const cleaned = (name || 'file').replace(/[^\w.\- ]+/g, '_');
  // Keep the tail so the extension survives truncation.
  return cleaned.length > 80 ? cleaned.slice(-80) : cleaned;
}

function formatMb(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function useFutureBotAttachments(sessionId) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [attachments, setAttachments] = useState([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const patchAttachment = useCallback((id, updates) => {
    // No-op if the chip was removed mid-upload.
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  const uploadOne = useCallback(
    async (rawFile) => {
      let file = rawFile;
      const isPdf = file.type === 'application/pdf';
      const isImage = (file.type || '').startsWith('image/');
      if (!isPdf && !isImage) {
        toast({
          title: 'Unsupported file',
          description: `${file.name}: FutureBot accepts PDFs and images.`,
          status: 'warning',
          duration: 4000,
        });
        return;
      }
      if (isPdf && file.size > MAX_PDF_BYTES) {
        toast({
          title: 'PDF too large',
          description: `${file.name} is ${formatMb(file.size)} — the limit is 20 MB.`,
          status: 'warning',
          duration: 4000,
        });
        return;
      }
      if (isImage) {
        try {
          file = await normalizeImageForChat(file);
        } catch (err) {
          toast({ title: 'Image not usable', description: err.message, status: 'warning', duration: 5000 });
          return;
        }
      }

      const id = generateId();
      const storagePath = `futurebot-uploads/${currentUser.uid}/${sessionId}/${Date.now()}_${safeFileName(file.name)}`;
      setAttachments((prev) => [
        ...prev,
        {
          id,
          name: rawFile.name || file.name,
          size: file.size,
          mimeType: file.type,
          status: 'uploading',
          progress: 0,
          storagePath,
          gsUri: `gs://${firebaseConfig.storageBucket}/${storagePath}`,
          downloadURL: null,
        },
      ]);

      try {
        const storageRef = firebase.storage().ref(storagePath);
        const uploadTask = storageRef.put(file);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snap) => patchAttachment(id, { progress: Math.round((snap.bytesTransferred / snap.totalBytes) * 100) }),
            reject,
            resolve
          );
        });
        const downloadURL = await storageRef.getDownloadURL();
        patchAttachment(id, { status: 'done', progress: 100, downloadURL });
      } catch (err) {
        console.error('[FutureBot] Attachment upload failed:', err);
        removeAttachment(id);
        toast({
          title: 'Upload failed',
          description: `${file.name}: ${err.message || 'unknown error'}`,
          status: 'error',
          duration: 5000,
        });
      }
    },
    [currentUser?.uid, sessionId, toast, patchAttachment, removeAttachment]
  );

  const addFiles = useCallback(
    (fileList) => {
      if (!currentUser?.uid) return;
      const incoming = Array.from(fileList || []);
      if (incoming.length === 0) return;

      const room = Math.max(0, MAX_ATTACHMENTS - attachmentsRef.current.length);
      if (incoming.length > room) {
        toast({
          title: 'Attachment limit',
          description: `You can attach up to ${MAX_ATTACHMENTS} files per message.`,
          status: 'warning',
          duration: 4000,
        });
      }
      incoming.slice(0, room).forEach((file) => uploadOne(file));
    },
    [currentUser?.uid, toast, uploadOne]
  );

  const isUploading = useMemo(() => attachments.some((a) => a.status === 'uploading'), [attachments]);

  return { attachments, isUploading, addFiles, removeAttachment, clearAttachments };
}

export default useFutureBotAttachments;
